// client/src/components/custom/SchemaLibrary.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, InputAdornment, Grid, Card,
  CardContent, CardActions, Button, Chip, Alert, CircularProgress,
  ToggleButtonGroup, ToggleButton, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HubIcon from '@mui/icons-material/Hub';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AppBarHeader from '../AppBarHeader';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { jwtDecode } from 'jwt-decode';

export default function SchemaLibrary() {
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();

  const token = localStorage.getItem('token');
  const userDecoded = token ? jwtDecode(token) : null;
  const userId = userDecoded?.id ?? null;
  const isAdmin = userDecoded?.isAdmin ?? false;
  const handleLogout = () => { localStorage.removeItem('token'); navigate('/login'); };
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('all');
  const [adopting, setAdopting] = useState(null);
  const [adoptError, setAdoptError] = useState('');
  const [adoptedIds, setAdoptedIds] = useState(new Set());
  const [confirmSchema, setConfirmSchema] = useState(null);

  const loadLibrary = useCallback(() => {
    setLoading(true);
    Promise.all([
      axiosInstance.get(`/custom-schemas/library?source=${source}`),
      axiosInstance.get('/custom-schemas/adopted'),
    ])
      .then(([libRes, adoptedRes]) => {
        setSchemas(libRes.data);
        setAdoptedIds(new Set(adoptedRes.data.map(a => a.schemaId?._id?.toString() ?? a.schemaId?.toString())));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [source]);

  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  if (!featureFlags?.enableCustomCollections) {
    return (
      <div>
        <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
        <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">Custom Collections is not enabled.</Typography>
        </Container>
      </div>
    );
  }

  const executeAdopt = async (schema) => {
    setAdopting(schema._id);
    setAdoptError('');
    try {
      await axiosInstance.post(`/custom-schemas/${schema._id}/adopt`);
      setAdoptedIds(prev => new Set([...prev, schema._id]));
      navigate(`/custom/${schema._id}`);
    } catch (err) {
      setAdoptError(err.response?.data?.error || 'Failed to adopt schema');
      setAdopting(null);
    }
  };

  const handleAdoptClick = (schema) => {
    if (schema.isRemote) {
      setConfirmSchema(schema);
    } else {
      executeAdopt(schema);
    }
  };

  const handleConfirmAdopt = () => {
    const schema = confirmSchema;
    setConfirmSchema(null);
    executeAdopt(schema);
  };

  const filtered = schemas.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <Button variant="outlined" sx={{ mb: 2 }} onClick={() => navigate(-1)}>
          ← Back
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          Schema Library
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          Schemas are user-generated content. They may change or be removed by their creator at any time.
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search schemas…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 220 }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={source}
              onChange={(_, val) => { if (val) setSource(val); }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="local">Local</ToggleButton>
              <ToggleButton value="remote">
                <HubIcon sx={{ fontSize: 16, mr: 0.5 }} />Remote
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/custom-schemas/new')}>
            Create Your Own
          </Button>
        </Box>

        {adoptError && <Alert severity="error" sx={{ mb: 2 }}>{adoptError}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            {search ? 'No schemas match your search.' : 'No schemas in the library yet.'}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {filtered.map(schema => (
              <Grid item xs={12} sm={6} md={4} key={schema._id}>
                <SchemaCard
                  schema={schema}
                  isAdopted={adoptedIds.has(schema._id)}
                  adopting={adopting === schema._id}
                  onAdopt={handleAdoptClick}
                  onNavigate={navigate}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Remote adoption confirmation dialog */}
      <Dialog open={!!confirmSchema} onClose={() => setConfirmSchema(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adopt Remote Schema?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{confirmSchema?.name}</strong> is hosted on{' '}
            <strong>{confirmSchema?.remoteDomain}</strong>. Remote schemas sync daily and may be
            updated or removed by their creator. Your collection data stays local regardless.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSchema(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmAdopt}>Use this collection</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function SchemaCard({ schema, isAdopted, adopting, onAdopt, onNavigate }) {
  const isRemote = !!schema.isRemote;

  return (
    <Card sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 3,
    }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          {schema.icon && (
            <Typography variant="h5" component="span" sx={{ flexShrink: 0 }}>{schema.icon}</Typography>
          )}
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>{schema.name}</Typography>
        </Box>

        {schema.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {schema.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <Chip size="small" label={`${schema.fields?.length ?? 0} fields`} />
          <Chip size="small" label={`${schema.adopterCount ?? 0} adopters`} variant="outlined" />
          {isRemote && (
            <Chip
              size="small"
              icon={<HubIcon sx={{ fontSize: 14 }} />}
              label={schema.remoteDomain}
              color="info"
              variant="outlined"
            />
          )}
        </Box>

        <Typography
          variant="caption" color="text.secondary"
          sx={{ mt: 1, display: 'block', cursor: isRemote ? 'default' : 'pointer', '&:hover': isRemote ? {} : { textDecoration: 'underline' } }}
          onClick={() => { if (!isRemote && schema.ownerId?._id) onNavigate(`/users/${schema.ownerId._id}/collections`); }}
        >
          by {schema.ownerDisplay || schema.ownerId?.username || 'unknown'}
        </Typography>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2 }}>
        {isAdopted ? (
          <Button size="small" variant="outlined" onClick={() => onNavigate(`/custom/${schema._id}`)}>
            Open Collection
          </Button>
        ) : (
          <Button
            size="small" variant="contained"
            disabled={adopting}
            onClick={() => onAdopt(schema)}
          >
            {adopting ? <CircularProgress size={16} /> : 'Use this collection'}
          </Button>
        )}
        {isRemote && (
          <Chip
            size="small"
            icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
            label="External"
            color="warning"
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        )}
      </CardActions>
    </Card>
  );
}
